import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { marked } from 'marked';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ChatbotService, A2UIEvent, ChatHistoryItem } from '../../services/chatbot.service';
import { NzMessageService } from 'ng-zorro-antd/message';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  htmlContent?: string;
  timestamp: Date;
  isStreaming?: boolean;
  imageUrl?: SafeUrl;
  fileName?: string;
}

interface QuickModule {
  title: string;
  icon: string;
  bgColor: string;
  tagColor?: string;
  accentColor: string;
  description: string;
  items: string[];
  itemIcons: string[];
}

interface HistoryItem {
  id?: number;
  title: string;
  time: string;
  category?: string | null;
  conversationId?: string;
  active?: boolean;
}

interface CategoryGroup {
  category: string;
  tagColor: string;
  collapsed: boolean;
  items: HistoryItem[];
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  isOpen = false;
  isSidebarCollapsed = false;
  showQuickModules = false;
  /** 拖拽相关状态 */
  chatPosition: { x: number | null; y: number | null } = { x: null, y: null };
  isDragging = false;
  private dragOffset = { x: 0, y: 0 };

  get chatWindowStyle(): { [key: string]: string } {
    if (this.chatPosition.x !== null && this.chatPosition.y !== null) {
      return {
        top: this.chatPosition.y + 'px',
        left: this.chatPosition.x + 'px',
        bottom: 'auto',
        right: 'auto'
      };
    }
    return {};
  }

  /**
   * 通用拖拽手柄：topbar / sidebar / footer 的空白处均可拖动窗口。
   * 命中排除选择器（按钮、输入框、链接、可点击业务项）时不启动拖拽，保证原有交互不受影响。
   */
  private readonly dragExcludeSelector = [
    'button',
    'input',
    'textarea',
    'a',
    '[role="button"]',
    '.chatbot-sidebar__history-item',
    '.chatbot-sidebar__tag-item',
    '.chatbot-footer__tag'
  ].join(',');

  onDragHandleMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest(this.dragExcludeSelector)) return;
    this.isDragging = true;
    const windowEl = (event.currentTarget as HTMLElement).closest('.chatbot-window') as HTMLElement;
    if (!windowEl) return;
    const rect = windowEl.getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    if (this.chatPosition.x === null) {
      const wrapper = windowEl.closest('.chatbot-wrapper') as HTMLElement;
      const wrapperRect = wrapper.getBoundingClientRect();
      // 将窗口的视口坐标转换为相对于 wrapper 的坐标
      this.chatPosition.x = rect.left - wrapperRect.left;
      this.chatPosition.y = rect.top - wrapperRect.top;
    }
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const windowEl = document.querySelector('.chatbot-window') as HTMLElement;
    const wrapper = windowEl?.closest('.chatbot-wrapper') as HTMLElement;
    if (!wrapper) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    this.chatPosition.x = event.clientX - wrapperRect.left - this.dragOffset.x;
    this.chatPosition.y = event.clientY - wrapperRect.top - this.dragOffset.y;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  inputMessage = '';
  messages: ChatMessage[] = [];
  isLoading = false;
  isHistoryLoading = false;
  isMessagesLoading = false;
  conversationId: string | null = null;
  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;
  safeFilePreviewUrl: SafeUrl | null = null;
  currentChatTitle = '新聊天';
  currentHistoryId: number | null = null;
  private isFirstRoundDone = false;
  private pendingImageBase64: string | null = null;

  private chatSubscription: Subscription | null = null;
  private shouldScrollToBottom = false;

  constructor(private chatbotService: ChatbotService, private message: NzMessageService, private sanitizer: DomSanitizer) {}

  historyList: HistoryItem[] = [];
  categoryGroups: CategoryGroup[] = [];

  quickModules: QuickModule[] = [
    {
      title: '热门动态',
      icon: 'trending_up',
      bgColor: '#FFCEAC',
      tagColor: '#4b2c6d',
      accentColor: '#9E2896',
      description: '获取最新热门更新信息',
      items: ['查看公告', '今日订单信息'],
      itemIcons: ['arrow_forward', 'newspaper']
    },
    {
      title: '订单管理',
      icon: 'description',
      bgColor: '#EFDDEE',
      tagColor: '#005691',
      accentColor: '#283587',
      // accentColor: '#18837E',
      description: '快速追踪物流状态，查询订单清单',
      items: ['查询产品库存', '获取本月提单清单'],
      itemIcons: ['location_on', 'inventory_2']
    },
    {
      title: '产品手册',
      icon: 'menu_book',
      bgColor: '#B9EFCB',
      tagColor: '#00884a',
      accentColor: '#007BC0',
      description: '根据VIN码，车型查询产品信息',
      items: ['搜索车型适配零件信息', '获取本月电子目录excel清单'],
      itemIcons: ['search', 'download']
    },
    {
      title: '财务概览',
      icon: 'account_balance_wallet',
      bgColor: '#99D2F2',
      tagColor: '#ffcf00',
      accentColor: '#FF8726',
      description: '查询信用额度，未清发票信息。',
      items: ['查看待付款金额', '查询信用额度'],
      itemIcons: ['receipt_long', 'payments']
    },
    {
      title: '索赔和质保',
      icon: 'verified_user',
      bgColor: '#FFDADA',
      tagColor: '#00335e',
      accentColor: '#18837E',
      description: '提供产品技术支持和质保索赔工单查询',
      items: ['质保工单进度查询'],
      itemIcons: ['report_problem']
    },
    {
      title: '人工支持',
      icon: 'support_agent',
      bgColor: '#D0D0D0',
      tagColor: '#64748b',
      accentColor: '#71767C',
      description: '问题解决不了，联系博世人工。',
      items: ['订单相关人工联系方式', '售后质保人工联系方式'],
      itemIcons: ['chat', 'assignment']
    }
  ];

  quickTags: string[] = ['查看本季发票汇总', '上报紧急零件缺失'];

  ngOnInit(): void {
    marked.setOptions({
      breaks: true,
      gfm: true
    });
  }

  ngOnDestroy(): void {
    this.chatSubscription?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private createWelcomeMessage(content?: string): ChatMessage {
    const message = content || '您好！我是您的专属经销商AI助手。你可以直接点击上方的推荐模块以快速了解常用功能，或者在下方输入框中输入您的问题，例如："我想知道订单#SO2024001的最新状态"。';

    return {
      role: 'assistant',
      content: message,
      htmlContent: marked.parse(message) as string,
      timestamp: new Date()
    };
  }

  private resetCurrentChatState(): void {
    this.chatSubscription?.unsubscribe();
    this.conversationId = null;
    this.currentHistoryId = null;
    this.currentChatTitle = '新聊天';
    this.isFirstRoundDone = false;
    this.isLoading = false;
    this.isMessagesLoading = false;
    this.inputMessage = '';
    this.pendingImageBase64 = null;
    this.showQuickModules = false;
    this.removeSelectedFile();
    this.messages = [];
    this.historyList = this.historyList.map(h => ({ ...h, active: false }));
    this.buildCategoryGroups();
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch (err) {}
  }

  private getCurrentUserId(): string {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return '';
      const data = JSON.parse(raw);
      const id = data?.queryUserPageInfoResDTO?.id;
      return id != null ? String(id) : '';
    } catch {
      return '';
    }
  }

  getUserInitials(): string {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return 'U';
      const data = JSON.parse(raw);
      const dto = data?.queryUserPageInfoResDTO;
      const last = dto?.lastName?.substring(0, 1) || '';
      const first = dto?.firstName?.substring(0, 1) || '';
      return (last + first) || 'U';
    } catch {
      return 'U';
    }
  }

  private loadChatHistory(): void {
    this.isHistoryLoading = true;
    this.chatbotService.getChatHistory().subscribe({
      next: (res) => {
        this.isHistoryLoading = false;
        if (res.rtnCode === 10000 && res.rtnData) {
          this.historyList = res.rtnData.map(item => ({
            id: item.id,
            title: item.title || '新聊天',
            time: this.formatTime(item.updateTime),
            category: item.category,
            conversationId: item.conversationId,
            active: false
          }));
          this.buildCategoryGroups();
        }
      },
      error: (err) => {
        this.isHistoryLoading = false;
        console.warn('[Chatbot] Failed to load history:', err);
      }
    });
  }

  private formatTime(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${y}/${mo}/${day} ${h}:${min}`;
    } catch {
      return dateStr;
    }
  }

  private readonly VALID_CATEGORIES = ['订单管理', '产品手册', '财务概览', '索赔和质保', '人工支持'];

  /** 后端返回的分类值 → 前端显示标签的映射 */
  private readonly CATEGORY_MAP: Record<string, string> = {
    '订单相关': '订单管理',
    '电子目录': '产品手册',
    '财务对账': '财务概览',
    '理赔质保': '索赔和质保',
    '人工支持': '人工支持'
  };

  /** 将后端返回的分类值映射为前端标签，未匹配则返回原值 */
  private mapCategory(raw: string | null | undefined): string {
    if (!raw) return '';
    return this.CATEGORY_MAP[raw] || raw;
  }

  private getCategoryTagColor(category: string): string {
    const module = this.quickModules.find(m => m.title === category);
    return module?.tagColor || '#000000';
  }

  private buildCategoryGroups(): void {
    // Initialize all 5 valid categories with empty item arrays
    const groups: Map<string, HistoryItem[]> = new Map(
      this.VALID_CATEGORIES.map(cat => [cat, []])
    );
    const noCategoryKey = '未分类';
    for (const item of this.historyList) {
      const mappedCat = this.mapCategory(item.category);
      const cat = mappedCat && this.VALID_CATEGORIES.includes(mappedCat)
        ? mappedCat
        : noCategoryKey;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(item);
    }
    // Preserve existing collapsed state
    const existingCollapsed = new Map(this.categoryGroups.map(g => [g.category, g.collapsed]));
    this.categoryGroups = Array.from(groups.entries()).map(([cat, items]) => ({
      category: cat,
      tagColor: this.getCategoryTagColor(cat),
      collapsed: existingCollapsed.has(cat) ? existingCollapsed.get(cat)! : true,
      items
    }));
  }

  toggleCategoryCollapse(group: CategoryGroup): void {
    group.collapsed = !group.collapsed;
  }

  /** 根据登录状态返回可展示的快捷模块列表 */
  getDisplayedModules(): QuickModule[] {
    const token = localStorage.getItem('token');
    if (token) {
      return this.quickModules;
    }
    // 未登录只展示公开模块
    const publicTitles = ['产品手册', '索赔和质保', '人工支持'];
    return this.quickModules
      .filter(m => publicTitles.includes(m.title))
      .map(m => {
        if (m.title === '索赔和质保') {
          // 未登录时不展示'质保工单进度查询'
          return {
            ...m,
            items: m.items.filter(item => item !== '质保工单进度查询'),
            itemIcons: m.itemIcons.filter((_, i) => m.items[i] !== '质保工单进度查询')
          };
        }
        return m;
      })
  }

  private saveChatHistory(onSaved?: () => void): void {
    const userId = this.getCurrentUserId();
    if (!userId || this.messages.length <= 1) return;

    const messagesToSave = this.messages.map(m => ({
      role: m.role,
      content: m.content,
      timestamp: m.timestamp.toISOString()
    }));

    // Embed base64 image data into user message content for persistence
    if (this.pendingImageBase64) {
      for (let i = messagesToSave.length - 1; i >= 0; i--) {
        if (messagesToSave[i].role === 'user') {
          const msg = messagesToSave[i];
          const fileName = this.messages[i].fileName;
          if (fileName) {
            msg.content = msg.content.replace(
              `[${fileName}]`,
              `![${fileName}](${this.pendingImageBase64})`
            );
          }
          break;
        }
      }
      this.pendingImageBase64 = null;
    }

    this.chatbotService.saveChatHistory({
      userId,
      conversationId: this.conversationId || undefined,
      messages: messagesToSave
    }).subscribe({
      next: (res) => {
        if (res.rtnCode === 10000) {
          if (res.rtnData) {
            this.currentHistoryId = res.rtnData;
          }
          this.updateHistoryListItem();
          onSaved?.();
        }
      },
      error: (err) => console.warn('[Chatbot] Failed to save history:', err)
    });
  }

  private updateHistoryListItem(): void {
    const existing = this.historyList.find(h => h.conversationId === this.conversationId);
    if (!existing) {
      const newItem: HistoryItem = {
        id: this.currentHistoryId!,
        title: this.currentChatTitle,
        time: this.formatTime(new Date().toISOString()),
        category: null,
        conversationId: this.conversationId || undefined,
        active: true
      };
      this.historyList = [newItem, ...this.historyList.map(h => ({ ...h, active: false }))];
    } else {
      existing.title = this.currentChatTitle;
      existing.time = this.formatTime(new Date().toISOString());
      existing.active = true;
      this.historyList = this.historyList.map(h => ({ ...h, active: h.conversationId === this.conversationId }));
    }
    this.buildCategoryGroups();
  }

  private triggerTitleGeneration(firstUserMessage: string): void {
    if (!this.currentHistoryId) return;
    this.chatbotService.generateTitle(this.currentHistoryId, firstUserMessage).subscribe({
      next: (res) => {
        if (res.rtnCode === 10000 && res.rtnData) {
          const { title, category } = res.rtnData;
          if (title) {
            this.currentChatTitle = title;
          }
          // Update historyList item
          const item = this.historyList.find(
            h => h.id === this.currentHistoryId || h.conversationId === this.conversationId
          );
          const mappedCategory = this.mapCategory(category);
          if (item) {
            if (title) item.title = title;
            if (category !== undefined) item.category = mappedCategory;
          }
          this.buildCategoryGroups();
        }
      },
      error: (err) => console.warn('[Chatbot] Failed to generate title:', err)
    });
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.loadChatHistory();
      // 默认位置由 CSS bottom:70px; right:0 控制（相对于 chatbot-wrapper）
    }
  }

  closeChat(): void {
    this.resetCurrentChatState();
    this.isOpen = false;
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  startNewChat(): void {
    this.resetCurrentChatState();
  }

  loadHistoryChat(item: HistoryItem): void {
    if (!item.id) return;

    // Mark as active
    this.historyList = this.historyList.map(h => ({ ...h, active: h.id === item.id }));
    this.buildCategoryGroups();
    this.conversationId = item.conversationId || null;
    this.currentHistoryId = item.id;
    this.currentChatTitle = item.title;
    this.isFirstRoundDone = true;
    this.chatSubscription?.unsubscribe();
    this.inputMessage = '';

    this.isMessagesLoading = true;
    this.messages = [];
    this.chatbotService.getChatMessages(item.id).subscribe({
      next: (res) => {
        this.isMessagesLoading = false;
        if (res.rtnCode === 10000 && res.rtnData?.messages?.length) {
          this.messages = res.rtnData.messages.map(m => {
            const msg: ChatMessage = {
              role: m.role as 'user' | 'assistant',
              content: m.content,
              htmlContent: m.role === 'assistant' ? (marked.parse(m.content) as string) : undefined,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
            };
            // Extract embedded base64 image from user message content
            if (m.role === 'user' && m.content) {
              const match = m.content.match(/!\[([^\]]*)\]\((data:image\/[^)]+)\)/);
              if (match) {
                msg.imageUrl = this.sanitizer.bypassSecurityTrustUrl(match[2]);
                msg.fileName = match[1] || undefined;
                // Strip image markdown, keep caption text
                msg.content = m.content.replace(match[0], '').trim();
              }
            }
            return msg;
          });
          this.shouldScrollToBottom = true;
        } else {
          this.messages = [this.createWelcomeMessage('您好！我是您的专属经销商AI助手。继续之前的对话，请输入您的问题。')];
        }
      },
      error: (err) => {
        this.isMessagesLoading = false;
        console.warn('[Chatbot] Failed to load chat messages:', err);
        this.messages = [this.createWelcomeMessage('加载历史消息失败，请重试。')];
      }
    });
  }

  deleteHistoryItem(item: HistoryItem, event: MouseEvent): void {
    event.stopPropagation();
    if (!item.id) return;
    this.chatbotService.deleteHistory(item.id).subscribe({
      next: (res) => {
        if (res.rtnCode === 10000) {
          this.message.success('删除成功');
          if (item.active) {
            this.startNewChat();
          }
          this.loadChatHistory();
        }
      },
      error: (err) => {
        console.warn('[Chatbot] Failed to delete history:', err);
        this.message.error('删除失败，请重试');
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      if (this.selectedFile.type.startsWith('image/')) {
        this.filePreviewUrl = URL.createObjectURL(this.selectedFile);
        this.safeFilePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.filePreviewUrl);
      } else {
        this.filePreviewUrl = null;
        this.safeFilePreviewUrl = null;
      }
    }
    input.value = '';
  }

  removeSelectedFile(): void {
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.safeFilePreviewUrl = null;
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  sendMessage(): void {
    this.hideQuickModules();
    if (this.selectedFile) {
      this.sendImageMessage();
      return;
    }
    if (!this.inputMessage.trim()) return;

    const userMsg = this.inputMessage;
    const isFirst = !this.isFirstRoundDone;

    this.messages.push({
      role: 'user',
      content: userMsg,
      timestamp: new Date()
    });

    this.inputMessage = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;

    this.chatSubscription?.unsubscribe();

    const assistantMsg: ChatMessage = {
      role: 'assistant',
      content: '',
      htmlContent: '',
      timestamp: new Date(),
      isStreaming: true
    };
    this.messages.push(assistantMsg);

    this.chatSubscription = this.chatbotService.chat({
      query: userMsg,
      conversationId: this.conversationId || undefined,
      user: localStorage.getItem('username') || undefined,
      portal: localStorage.getItem('portalName') || undefined,
    }).subscribe({
      next: (event: A2UIEvent) => {
        if (event.type === 'content_block_delta') {
          if (event.delta.type === 'text_delta' && event.delta.text) {
            assistantMsg.content += event.delta.text;
            assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
            this.shouldScrollToBottom = true;
          }
          if (event.conversation_id) {
            this.conversationId = event.conversation_id;
          }
        } else if (event.type === 'message_end') {
          assistantMsg.isStreaming = false;
          if (event.metadata?.conversation_id) {
            this.conversationId = event.metadata.conversation_id;
          }
          this.isLoading = false;
          // Save history after every round; generate title after first round
          if (isFirst) {
            this.isFirstRoundDone = true;
            const firstRoundContent = `${userMsg}。 ${assistantMsg.content}`;
            this.saveChatHistory(() => this.triggerTitleGeneration(firstRoundContent));
          } else {
            this.saveChatHistory();
          }
        } else if (event.type === 'error') {
          assistantMsg.content = event.message || '抱歉，请求出现错误，请稍后重试。';
          assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
          assistantMsg.isStreaming = false;
          this.isLoading = false;
        }
      },
      error: (err) => {
        const msg = err?.message?.includes('超时')
          ? '请求超时，请重新发送消息。'
          : '网络连接异常，请检查网络后重试。';
        assistantMsg.content = msg;
        assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
        assistantMsg.isStreaming = false;
        this.isLoading = false;
      },
      complete: () => {
        assistantMsg.isStreaming = false;
        if (!assistantMsg.htmlContent && assistantMsg.content) {
          assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
        }
        this.isLoading = false;
      }
    });
  }

  onQuickItemClick(item: string): void {
    this.inputMessage = item;
    this.hideQuickModules();
    this.sendMessage();
  }

  toggleQuickModules(): void {
    this.showQuickModules = !this.showQuickModules;
    if (this.showQuickModules) {
      this.shouldScrollToBottom = true;
    }
  }

  hideQuickModules(): void {
    this.showQuickModules = false;
  }

  private async sendImageMessage(): Promise<void> {
    const file = this.selectedFile!;
    const fileName = file.name;
    const queryText = this.inputMessage.trim();
    const type = file.type.startsWith('image/') ? 'image' : 'document';
    const isFirst = !this.isFirstRoundDone;

    // For images, convert to base64 for persistence in history
    this.pendingImageBase64 = null;
    if (type === 'image') {
      try {
        this.pendingImageBase64 = await this.readFileAsBase64(file);
      } catch (e) {
        console.warn('[Chatbot] Failed to read image as base64:', e);
      }
    }

    // Display content stays clean (no base64)
    const displayContent = queryText ? `[${fileName}] ${queryText}` : `[${fileName}]`;

    const userMsg: ChatMessage = {
      role: 'user',
      content: displayContent,
      timestamp: new Date(),
      fileName
    };
    if (type === 'image' && this.filePreviewUrl) {
      userMsg.imageUrl = this.sanitizer.bypassSecurityTrustUrl(this.filePreviewUrl);
      // Prevent removeSelectedFile from revoking the URL that's still used in the message
      this.filePreviewUrl = null;
    }
    this.messages.push(userMsg);

    const firstUserContent = displayContent;
    this.removeSelectedFile();
    this.inputMessage = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;

    this.chatSubscription?.unsubscribe();

    const assistantMsg: ChatMessage = {
      role: 'assistant',
      content: '',
      htmlContent: '',
      timestamp: new Date(),
      isStreaming: true
    };
    this.messages.push(assistantMsg);

    const user = localStorage.getItem('username') || undefined;

    this.chatSubscription = this.chatbotService.uploadImage(file, type, queryText, user).subscribe({
      next: (event: A2UIEvent) => {
        if (event.type === 'content_block_delta') {
          if (event.delta.type === 'text_delta' && event.delta.text) {
            assistantMsg.content += event.delta.text;
            assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
            this.shouldScrollToBottom = true;
          }
          if (event.conversation_id) {
            this.conversationId = event.conversation_id;
          }
        } else if (event.type === 'message_end') {
          assistantMsg.isStreaming = false;
          this.isLoading = false;
          if (isFirst) {
            this.isFirstRoundDone = true;
            const firstRoundContent = `${firstUserContent}。 ${assistantMsg.content}`;
            this.saveChatHistory(() => this.triggerTitleGeneration(firstRoundContent));
          } else {
            this.saveChatHistory();
          }
        } else if (event.type === 'error') {
          assistantMsg.content = event.message || '抱歉，文件处理出现错误，请稍后重试。';
          assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
          assistantMsg.isStreaming = false;
          this.isLoading = false;
        }
      },
      error: (err) => {
        const msg = err?.message?.includes('超时')
          ? '文件处理超时，请重新上传。'
          : '网络连接异常，请检查网络后重试。';
        assistantMsg.content = msg;
        assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
        assistantMsg.isStreaming = false;
        this.isLoading = false;
      },
      complete: () => {
        assistantMsg.isStreaming = false;
        if (!assistantMsg.htmlContent && assistantMsg.content) {
          assistantMsg.htmlContent = marked.parse(assistantMsg.content) as string;
        }
        this.isLoading = false;
      }
    });
  }

  onTagClick(tag: string): void {
    this.inputMessage = tag;
    this.sendMessage();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }
}
